import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  BadgeCheck,
  Bot,
  ChevronLeft,
  CircleX,
  Eye,
  Footprints,
  Loader2,
  MailPlus,
  PenLine,
  RotateCcw,
  Send,
} from 'lucide-react'
import { useState } from 'react'
import {
  AcceptancePanel,
  HandoffPanel,
  LinkPanel,
  OnboardingPanel,
  PriceSummary,
  Timeline,
} from '@/components/quotes/quote-panels'
import { QuoteEditor } from '@/components/quotes/quote-editor'
import { QuoteContractLink } from '@/components/contracts/quote-link'
import { PhotosPanel, SurveyPanel } from '@/components/survey/quote-panels'
import { SignatureField, type SignatureValue } from '@/components/quotes/signature-field'
import { StatusBadge } from '@/components/quotes/status-badge'
import { FormField, TextInput } from '@/components/settings/form-kit'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { quoteQuery, ukDate, useQuoteAction, type QuoteDetail } from '@/lib/quotes'
import { settingsQuery } from '@/lib/settings'
import { useTitle } from '@/lib/use-title'

export const Route = createFileRoute('/_app/quotes/$id')({ component: QuotePage })

const SENDERS = ['owner', 'platform_admin', 'commercial_manager']
const APPROVERS = ['owner', 'commercial_manager']
const FOLLOWERS = ['owner', 'platform_admin', 'commercial_manager', 'sales_admin']

function QuotePage() {
  const { id } = Route.useParams()
  const { me } = Route.useRouteContext()
  const qc = useQueryClient()
  const { data: q, isPending, isError, error } = useQuery(quoteQuery(id))
  const act = useQuoteAction(id)
  const [dialog, setDialog] = useState<
    null | 'send' | 'reject' | 'withdraw' | 'revise' | 'follow-up'
  >(null)
  const [link, setLink] = useState<string | null>(null)
  useTitle(q ? `${q.reference} ${q.title}` : 'Quote')

  if (isError)
    return (
      <p role="alert" className="text-destructive">
        {error.message}
      </p>
    )
  if (isPending) return <Skeleton className="h-[70dvh] rounded-xl" />

  const canSend = me.roles.some((r) => SENDERS.includes(r))
  const canApproveRole =
    me.roles.some((r) => APPROVERS.includes(r)) && q.approval.approvers.some((a) => a.id === me.id)
  const nobodyCanApprove = q.approval.approvers.length === 0
  const others = q.approval.approvers.filter((a) => a.id !== me.id)
  const run = (action: string, body?: unknown, success?: string) =>
    act.mutate(
      { action, body, success },
      {
        onSuccess: (d) => {
          if (d.link) setLink(d.link)
          setDialog(null)
        },
      },
    )
  const draft = q.status === 'draft'
  const inReview = q.status === 'in_review'
  const byAgent = q.owner.startsWith('agent:')
  const canFollowUp = me.roles.some((r) => FOLLOWERS.includes(r))
  const issued = ['sent', 'viewed'].includes(q.status)

  return (
    <>
      <Link
        to="/quotes"
        className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Quotes
      </Link>
      <header className="mb-6 grid gap-4 lg:flex lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold md:text-[28px]">{q.title}</h1>
            <StatusBadge status={q.status} />
            {q.mode === 'onsite' && (
              <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[13px] font-medium">
                <Footprints className="size-3.5 text-primary" aria-hidden />
                On-site survey
              </span>
            )}
            {byAgent && (
              <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-accent/50 px-2 py-0.5 text-[13px] font-medium text-foreground">
                <Bot className="size-3.5 text-primary" aria-hidden />
                Prepared by {q.ownerName || 'Kira'}
              </span>
            )}
          </div>
          <p className="mt-1 text-[15px] text-muted-foreground">
            <span className="tabular font-medium text-foreground">{q.reference}</span>
            {q.revision > 1 && ` · revision ${q.revision}`} ·{' '}
            <Link
              to="/customers/$id"
              params={{ id: q.customerId }}
              className="underline-offset-4 hover:underline"
            >
              {q.customerName}
            </Link>{' '}
            · valid until {ukDate(q.validUntil)}
          </p>
          {q.approval.approvedByName && (
            <p className="mt-1 text-sm text-success">Approved by {q.approval.approvedByName}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="h-10"
            render={<Link to="/preview/$id" params={{ id }} />}
          >
            <Eye />
            Preview
          </Button>
          {draft && q.approval.required && (
            <Button
              className="h-10"
              disabled={act.isPending || nobodyCanApprove}
              onClick={() => run('submit', {}, 'Sent for approval')}
            >
              <Send />
              Ask for approval
            </Button>
          )}
          {inReview && q.approval.required && canApproveRole && (
            <>
              <Button variant="outline" className="h-10" onClick={() => setDialog('reject')}>
                <RotateCcw />
                Send back
              </Button>
              <Button
                className="h-10"
                disabled={act.isPending}
                onClick={() => run('approve', {}, 'Quote approved')}
              >
                <BadgeCheck />
                Approve
              </Button>
            </>
          )}
          {(((draft || inReview) && !q.approval.required) || q.status === 'approved') &&
            canSend && (
              <Button className="h-10" onClick={() => setDialog('send')}>
                <PenLine />
                Sign and send
              </Button>
            )}
          {(issued ||
            ['approved', 'declined', 'withdrawn', 'expired', 'in_review'].includes(q.status)) && (
            <Button variant="outline" className="h-10" onClick={() => setDialog('revise')}>
              <RotateCcw />
              Make changes
            </Button>
          )}
          {issued && canFollowUp && (
            <Button variant="outline" className="h-10" onClick={() => setDialog('follow-up')}>
              <MailPlus />
              Send a follow-up
            </Button>
          )}
          {issued && canSend && (
            <Button
              variant="outline"
              className="h-10 text-destructive"
              onClick={() => setDialog('withdraw')}
            >
              <CircleX />
              Withdraw
            </Button>
          )}
        </div>
      </header>

      {(draft || inReview) && q.approval.required && nobodyCanApprove && (
        <div
          role="alert"
          className="mb-6 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm"
        >
          <p className="font-semibold">Nobody can approve this quote yet</p>
          <p className="mt-1">
            You are the only person who could approve it, and people cannot approve their own
            quotes. Add an approver, such as Chris as an Owner, or allow people to approve their own
            quotes.
          </p>
          <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-medium">
            <Link
              to="/settings/$slug"
              params={{ slug: 'users' }}
              className="text-primary underline-offset-4 hover:underline"
            >
              Add a person
            </Link>
            <Link
              to="/settings/$slug"
              params={{ slug: 'approvals' }}
              className="text-primary underline-offset-4 hover:underline"
            >
              Change approval rules
            </Link>
          </p>
        </div>
      )}
      {inReview && q.approval.required && !canApproveRole && !nobodyCanApprove && (
        <p className="mb-6 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm">
          Waiting for approval from {others.map((a) => a.name).join(', ') || 'an approver'}.{' '}
          {q.events.some(
            (e) =>
              e.type === 'email_queued' &&
              (e.detail as { template?: string }).template === 'approval_requested',
          )
            ? 'They have been emailed.'
            : 'Let them know it is ready, as email is not switched on.'}
        </p>
      )}
      {draft && byAgent && canSend && (
        <p className="mb-6 flex items-start gap-2 rounded-lg border border-primary/25 bg-accent/40 px-4 py-3 text-sm">
          <Bot className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
          {q.ownerName || 'Kira'} drafted this quote. Check the prices and scope, then sign and send
          it yourself.
        </p>
      )}
      {draft && !canSend && (
        <p className="mb-6 rounded-lg border bg-muted px-4 py-3 text-sm">
          You can prepare this quote. An owner or commercial manager signs and sends it.
        </p>
      )}

      {draft && (
        <div className="mb-6">
          {q.mode === 'onsite' ? <SurveyPanel quote={q} /> : <PhotosPanel quote={q} />}
        </div>
      )}
      {draft ? (
        <QuoteEditor
          quote={q}
          me={me}
          onSaved={(saved) => qc.setQueryData(quoteQuery(id).queryKey, saved)}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="grid gap-4">
            {(issued || q.status === 'accepted') && (
              <LinkPanel
                quote={q}
                link={link}
                busy={act.isPending}
                onNewLink={() => run('share-link', {}, 'New link made')}
              />
            )}
            <QuoteContractLink quoteId={q.id} accepted={q.status === 'accepted'} />
            <AcceptancePanel quote={q} />
            <OnboardingPanel quote={q} />
            <HandoffPanel quote={q} />
            <PriceSummary quote={q} />
            {q.mode === 'onsite' ? <SurveyPanel quote={q} /> : <PhotosPanel quote={q} />}
          </div>
          <Timeline quote={q} />
        </div>
      )}

      <SendDialog
        open={dialog === 'send'}
        onOpenChange={(o) => setDialog(o ? 'send' : null)}
        quote={q}
        name={me.name}
        busy={act.isPending}
        onSend={(signature, role, emailClient, note) =>
          run(
            'send',
            { signature, signerRole: role, emailClient, note },
            emailClient ? 'Signed and emailed to the client' : 'Signed and ready to send',
          )
        }
      />
      <FollowUpDialog
        open={dialog === 'follow-up'}
        onOpenChange={(o) => setDialog(o ? 'follow-up' : null)}
        quote={q}
        busy={act.isPending}
        onSend={(message) => run('follow-up', { message }, 'Follow-up email on its way')}
      />
      <ReasonDialog
        open={dialog === 'reject'}
        onOpenChange={(o) => setDialog(o ? 'reject' : null)}
        title="Send back for changes"
        description="Say what needs changing. The author sees this on the quote."
        action="Send back"
        busy={act.isPending}
        onConfirm={(reason) => run('reject', { reason }, 'Sent back for changes')}
      />
      <ReasonDialog
        open={dialog === 'withdraw'}
        onOpenChange={(o) => setDialog(o ? 'withdraw' : null)}
        title="Withdraw this quote?"
        description="The client link stops working and the quote can no longer be accepted."
        action="Withdraw quote"
        destructive
        busy={act.isPending}
        onConfirm={(reason) => run('withdraw', { reason }, 'Quote withdrawn')}
      />
      <Dialog open={dialog === 'revise'} onOpenChange={(o) => setDialog(o ? 'revise' : null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Make changes?</DialogTitle>
            <DialogDescription>
              {issued
                ? 'The link you sent will stop accepting signatures. After your changes, you sign and send a new revision.'
                : 'The quote goes back to draft and will need approving again.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="h-10" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button
              className="h-10"
              disabled={act.isPending}
              onClick={() => run('revise', {}, 'Quote reopened for changes')}
            >
              {act.isPending && <Loader2 className="animate-spin" />}
              Make changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function SendDialog({
  open,
  onOpenChange,
  quote,
  name,
  busy,
  onSend,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  quote: QuoteDetail
  name: string
  busy: boolean
  onSend: (s: SignatureValue, role: string, emailClient: boolean, note: string) => void
}) {
  const settings = useQuery(settingsQuery)
  const canEmail = settings.data?.server.emailSending === 'live' && !!quote.contact?.email
  const [emailClient, setEmailClient] = useState(true)
  const [note, setNote] = useState('')
  const [sig, setSig] = useState<SignatureValue>({ type: 'typed', text: name, image: '' })
  const [role, setRole] = useState('Director')
  const [confirmed, setConfirmed] = useState(false)
  const ready =
    confirmed &&
    role.trim().length > 1 &&
    (sig.type === 'typed' ? sig.text.trim().length > 1 : !!sig.image)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Sign and send {quote.reference}</DialogTitle>
          <DialogDescription>
            You sign on behalf of HCD Cleaning. The proposal is then frozen and you get a private
            link for {quote.contact?.name ?? 'the client'}.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <FormField label="Your position">
            {(p) => <TextInput {...p} value={role} onChange={(e) => setRole(e.target.value)} />}
          </FormField>
          <SignatureField value={sig} onChange={setSig} name={name} />
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-4 accent-(--primary)"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            I have checked the prices, scope and documents, and HCD agrees to supply these services
            on these terms.
          </label>
          {canEmail ? (
            <div className="grid gap-3 rounded-lg border p-3">
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 accent-(--primary)"
                  checked={emailClient}
                  onChange={(e) => setEmailClient(e.target.checked)}
                />
                <span>
                  Email the proposal to {quote.contact!.name}{' '}
                  <span className="text-muted-foreground">({quote.contact!.email})</span>
                </span>
              </label>
              {emailClient && (
                <FormField label="Personal note (optional)" hint="Appears at the top of the email.">
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={3}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Lovely to meet you today. As promised, here is our proposal…"
                      className="text-base md:text-[15px]"
                    />
                  )}
                </FormField>
              )}
            </div>
          ) : (
            <p className="rounded-lg bg-muted px-3 py-2 text-[13px] text-muted-foreground">
              {settings.data?.server.emailSending !== 'live'
                ? 'Email sending is off, so you will copy the link and send it yourself.'
                : `${quote.contact?.name ?? 'The contact'} has no email address, so you will copy the link and send it yourself.`}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-10" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="h-10"
            disabled={!ready || busy}
            onClick={() => onSend(sig, role, canEmail && emailClient, note)}
          >
            {busy && <Loader2 className="animate-spin" />}
            {canEmail && emailClient ? 'Sign and email' : 'Sign and create link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  action,
  destructive,
  busy,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  title: string
  description: string
  action: string
  destructive?: boolean
  busy: boolean
  onConfirm: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <FormField label="Reason">
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="text-base md:text-[15px]"
            />
          )}
        </FormField>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-10" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            className="h-10"
            disabled={!reason.trim() || busy}
            onClick={() => onConfirm(reason.trim())}
          >
            {busy && <Loader2 className="animate-spin" />}
            {action}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function FollowUpDialog({
  open,
  onOpenChange,
  quote,
  busy,
  onSend,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  quote: QuoteDetail
  busy: boolean
  onSend: (message: string) => void
}) {
  const settings = useQuery(settingsQuery)
  const emailOn = settings.data?.server.emailSending === 'live'
  const email = quote.contact?.email
  const [message, setMessage] = useState('')
  const last = quote.events.find((e) => e.type === 'follow_up_sent')
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send a follow-up</DialogTitle>
          <DialogDescription>
            {email
              ? `A friendly reminder to ${quote.contact!.name} (${email}) with the same private link to view and sign.`
              : 'The client contact has no email address, so a follow-up cannot be emailed.'}
          </DialogDescription>
        </DialogHeader>
        {last && (
          <p className="rounded-lg bg-muted px-3 py-2 text-[13px] text-muted-foreground">
            Last follow-up {ukDate(last.at, true)}
            {last.actorLabel ? ` by ${last.actorLabel}` : ''}. One follow-up can be sent a day.
          </p>
        )}
        {!emailOn && (
          <p
            role="alert"
            className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm"
          >
            Email sending is off. Switch it on in Settings, Email.
          </p>
        )}
        <FormField label="Message (optional)" hint="Leave blank for a short standard reminder.">
          {(p) => (
            <Textarea
              {...p}
              rows={4}
              maxLength={2000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="I wanted to check you received our proposal and see if you have any questions."
              className="text-base md:text-[15px]"
            />
          )}
        </FormField>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-10" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="h-10"
            disabled={busy || !email || !emailOn}
            onClick={() => onSend(message.trim())}
          >
            {busy ? <Loader2 className="animate-spin" /> : <MailPlus />}
            Send follow-up
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
